import { useEffect } from "react";

interface DocumentMetaOptions {
  title?: string;
  description?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

function upsertMeta(name: string, content: string, property = false) {
  const attr = property ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.content = content;
}

function upsertLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

function removeLink(rel: string) {
  const el = document.querySelector(`link[rel="${rel}"]`);
  if (el) el.remove();
}

function upsertJsonLd(data: Record<string, unknown> | Record<string, unknown>[]) {
  let el = document.querySelector('script[data-meta="jsonld"]') as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-meta", "jsonld");
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeJsonLd() {
  const el = document.querySelector('script[data-meta="jsonld"]');
  if (el) el.remove();
}

export function useDocumentMeta(opts: DocumentMetaOptions) {
  useEffect(() => {
    const prev = document.title;

    if (opts.title) document.title = opts.title;
    if (opts.description) upsertMeta("description", opts.description);
    if (opts.canonical) upsertLink("canonical", opts.canonical);
    if (opts.ogTitle || opts.title) upsertMeta("og:title", opts.ogTitle || opts.title || "", true);
    if (opts.ogDescription || opts.description) upsertMeta("og:description", opts.ogDescription || opts.description || "", true);
    if (opts.ogImage) upsertMeta("og:image", opts.ogImage, true);
    if (opts.ogType) upsertMeta("og:type", opts.ogType, true);
    if (opts.twitterCard) upsertMeta("twitter:card", opts.twitterCard);
    if (opts.jsonLd) upsertJsonLd(opts.jsonLd);

    return () => {
      document.title = prev;
      removeLink("canonical");
      removeJsonLd();
    };
  }, [opts.title, opts.description, opts.canonical, opts.ogTitle, opts.ogDescription, opts.ogImage, opts.ogType, opts.twitterCard, opts.jsonLd]);
}
